import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import apiService from 'library/api-service';
import { ProjectGroup } from 'library/types';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { CreateProjectGroupDialog, UpdateProjectGroupDialog } from './components';

const ProjectGroups = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProjectGroup[]>([]);
  const [openDeleteProjectGroup, setOpenDeleteProjectGroup] = useState(false);
  const [openCreateProjectGroup, setOpenCreateProjectGroup] = useState(false);
  const [openUpdateProjectGroup, setOpenUpdateProjectGroup] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'projectGroups.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const getValues = async () => {
    try {
      setLoading(true);

      const response = await apiService.getProjectGroups();

      if (response.isSuccess) {
        setData(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleCloseDeleteProjectGroup = () => setOpenDeleteProjectGroup(false);
  const handleClickOpenDeleteProjectGroup = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenDeleteProjectGroup(true);
  };

  const handleCloseCreateProjectGroup = () => setOpenCreateProjectGroup(false);
  const handleClickOpenCreateProjectGroup = () => setOpenCreateProjectGroup(true);

  const handleCloseUpdateProjectGroup = () => setOpenUpdateProjectGroup(false);
  const handleClickOpenUpdateProjectGroup = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenUpdateProjectGroup(true);
  };

  // useEffect hooks
  useEffect(() => {
    getValues();
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-product-group']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateProjectGroup}>
              <FormattedMessage id="projectGroups.createProjectGroup" defaultMessage="Ürün Grubu Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-product-group']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateProjectGroup}>
              <FormattedMessage id="projectGroups.updateProjectGroup" defaultMessage="Ürün Grubu Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-product-group']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteProjectGroup}>
              <FormattedMessage id="projectGroups.deleteProjectGroup" defaultMessage="Ürün Grubu Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateProjectGroupDialog
        open={openCreateProjectGroup}
        handleClose={handleCloseCreateProjectGroup}
        refresh={getValues}
      />
      <UpdateProjectGroupDialog
        open={openUpdateProjectGroup}
        projectGroupId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateProjectGroup}
        refresh={getValues}
      />
      <DeleteDialog
        open={openDeleteProjectGroup}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteProjectGroup}
        refresh={getValues}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="projectGroups.deleteProjectGroup" defaultMessage="Ürün Grubu Sil" />}
        subTitle={
          <FormattedMessage
            id="projectGroups.deleteProjectGroupRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra ürün grubu veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteProjectGroup}
        successMessage={
          <FormattedMessage
            id="projectGroups.deleteProjectGroupCompleted"
            defaultMessage="Seçili ürün grubunu silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default ProjectGroups;
